import React, { useEffect, useRef, useMemo } from "react";
import { DEFAULT_COL_WIDTH, DEFAULT_ROW_HEIGHT, HEADER_COL_WIDTH, HEADER_ROW_HEIGHT } from "../../constants";
import { renderSimpleChart } from "../../charts/chartRenderer";

function ExcelChart({ chart, colWidths, rowHeights, zoomScale, hiddenCols, hiddenRows }) {
  const canvasRef = useRef(null);

  // Compute position and size synchronously to avoid a flash at (0,0)
  const { position, size } = useMemo(() => {
    if (!chart.range) return { position: { top: 0, left: 0 }, size: { width: 400, height: 300 } };

    const range = chart.range;
    const headerRowHeight = HEADER_ROW_HEIGHT;
    const headerColWidth = HEADER_COL_WIDTH;
    const isSparkline = !!chart.isSparkline;

    // Hidden rows/cols are NOT rendered by the grid, so they must not be
    // counted here either — otherwise every chart below/right of a hidden
    // row/col is offset by the hidden sizes.
    const isColHidden = (i) => !!(hiddenCols && hiddenCols.has && hiddenCols.has(i));
    const isRowHidden = (i) => !!(hiddenRows && hiddenRows.has && hiddenRows.has(i));

    // Calculate top-left position (sum of visible row/col sizes up to tl)
    let tlLeft = 0, tlTop = 0;
    for (let i = 0; i < (range.tl?.col || 0); i++) {
      if (!isColHidden(i)) tlLeft += (colWidths[i] || DEFAULT_COL_WIDTH);
    }
    for (let i = 0; i < (range.tl?.row || 0); i++) {
      if (!isRowHidden(i)) tlTop += (rowHeights[i] || DEFAULT_ROW_HEIGHT);
    }
    // Add EMU sub-cell offsets
    if (range.tl?.colOff) tlLeft += range.tl.colOff / 914400 * 96;
    if (range.tl?.rowOff) tlTop += range.tl.rowOff / 914400 * 96;

    // Add header offsets
    tlLeft += headerColWidth;
    tlTop += headerRowHeight;

    // Calculate bottom-right position. For two-cell anchors we have an
    // explicit "to" cell; for one-cell / absolute anchors the size is
    // expressed as an EMU extent (range.ext = { cx, cy }) instead.
    let brLeft = 0, brTop = 0;
    if (range.br) {
      for (let i = 0; i < range.br.col; i++) {
        if (!isColHidden(i)) brLeft += (colWidths[i] || DEFAULT_COL_WIDTH);
      }
      for (let i = 0; i < range.br.row; i++) {
        if (!isRowHidden(i)) brTop += (rowHeights[i] || DEFAULT_ROW_HEIGHT);
      }
      if (range.br.colOff) brLeft += range.br.colOff / 914400 * 96;
      if (range.br.rowOff) brTop += range.br.rowOff / 914400 * 96;

      brLeft += headerColWidth;
      brTop += headerRowHeight;
    }

    // EMU → pixels: 914400 EMU per inch, 96 px per inch (≈ 1 px = 9525 EMU)
    const EMU_TO_PX = 96 / 914400;

    // Minimum chart dimensions — sparklines must fit inside their host cell,
    // so they get no enforced minimum. Regular charts only get a tiny floor:
    // inflating a two-cell-anchored chart beyond its authored cells makes it
    // cover the wrong part of the sheet ("position is wrong").
    const minW = isSparkline ? 0 : 40;
    const minH = isSparkline ? 0 : 30;

    let width;
    let height;
    if (range.br) {
      width = Math.max(minW, brLeft - tlLeft);
      height = Math.max(minH, brTop - tlTop);
    } else if (range.ext && (range.ext.cx || range.ext.cy)) {
      width = Math.max(minW, Math.round(range.ext.cx * EMU_TO_PX));
      height = Math.max(minH, Math.round(range.ext.cy * EMU_TO_PX));
    } else {
      width = isSparkline ? (colWidths[range.tl?.col || 0] || DEFAULT_COL_WIDTH) : 400;
      height = isSparkline ? (rowHeights[range.tl?.row || 0] || DEFAULT_ROW_HEIGHT) : 300;
    }

    return { position: { top: tlTop, left: tlLeft }, size: { width, height } };
  }, [chart, colWidths, rowHeights, hiddenCols, hiddenRows]);

  // Scale both position AND size by zoomScale so overlay aligns with the zoomed table
  const scaledTop = Math.round(position.top * zoomScale);
  const scaledLeft = Math.round(position.left * zoomScale);
  const scaledWidth = Math.round(size.width * zoomScale);
  const scaledHeight = Math.round(size.height * zoomScale);

  // Render chart on canvas with device pixel ratio scaling for crisp output
  useEffect(() => {
    if (canvasRef.current && !chart.placeholder && scaledWidth > 0 && scaledHeight > 0) {
      const canvas = canvasRef.current;
      const dpr = window.devicePixelRatio || 1;
      // Set physical canvas resolution
      canvas.width = scaledWidth * dpr;
      canvas.height = scaledHeight * dpr;
      // Scale ctx so drawing commands use logical pixels
      const ctx = canvas.getContext('2d');
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      renderSimpleChart(canvas, chart, scaledWidth, scaledHeight);
    }
  }, [scaledWidth, scaledHeight, chart]);

  if (chart.placeholder) {
    return (
      <div
        className="excel-chart-placeholder"
        style={{
          position: 'absolute',
          top: scaledTop,
          left: scaledLeft,
          width: scaledWidth,
          height: scaledHeight,
          border: '2px dashed #ccc',
          backgroundColor: '#f9f9f9',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 10,
          pointerEvents: 'auto',
        }}
      >
        <div style={{ textAlign: 'center', color: '#666' }}>
          <div style={{ fontSize: '14px', fontWeight: 'bold' }}>📊 Chart</div>
          <div style={{ fontSize: '12px' }}>{chart.title || 'Chart'}</div>
        </div>
      </div>
    );
  }

  const isSparkline = !!chart.isSparkline;

  return (
    <div
      className={isSparkline ? "excel-sparkline-overlay" : "excel-chart-overlay"}
      style={{
        position: 'absolute',
        top: scaledTop,
        left: scaledLeft,
        width: scaledWidth,
        height: scaledHeight,
        zIndex: 10,
        pointerEvents: 'auto',
      }}
    >
      <canvas
        ref={canvasRef}
        style={{
          width: scaledWidth,
          height: scaledHeight,
          border: isSparkline ? 'none' : '1px solid #ddd',
          backgroundColor: isSparkline ? 'transparent' : '#fff',
          borderRadius: isSparkline ? 0 : '4px',
          boxShadow: isSparkline ? 'none' : '0 1px 4px rgba(0,0,0,0.08)',
          display: 'block',
        }}
      />
    </div>
  );
}

// PERF: memo'd — chart objects and size arrays are stable per sheet, so
// grid re-renders (selection, hover, scroll state) skip the whole chart
// subtree instead of re-running position math for every chart.
export default React.memo(ExcelChart);
